import React, { useState } from 'react';
import { submitFeedback } from '../api/client';

const ARTICLES = [
  {
    id: 'tour',
    icon: '🗺',
    title: 'Take the tour',
    summary: 'A quick walkthrough of the sidebar nav and main features.',
    isAction: true,
  },
  {
    id: 'dashboard',
    icon: '◉',
    title: 'Dashboard',
    summary: 'Your home base — recent sessions, insights, and at-a-glance progress.',
    body: [
      'The Dashboard is the first thing you see after logging in. It shows widgets you can rearrange or hide from Settings.',
      'Default widgets include: recent sessions, clients who haven\'t had a session in 30+ days, goal completion stats, and breakthrough quotes from the past week.',
      'Click any recent session to jump directly into it. Click a client name to open their full profile.',
    ],
  },
  {
    id: 'clients',
    icon: '👤',
    title: 'Clients',
    summary: 'Add and manage every client in your practice.',
    body: [
      'Each client has a profile with seven tabs: Overview, Sessions, Breakthroughs, Goals, Notes, Messages, and Avatar.',
      'When you add a new client, set their session type and modality (which can differ from your default modality). You can also paste intake background — it gets included in every AI summary going forward.',
      'Long-term goals (set on the Overview tab) shape the AI prompts so summaries connect what happened today to where the client wants to go.',
      'To give a client access to their portal, generate an access code on the Overview tab and share the link with them.',
    ],
  },
  {
    id: 'new-session',
    icon: '✨',
    title: 'New Session',
    summary: 'Record or paste a transcript — get a full AI summary in seconds.',
    body: [
      'New Session is a 4-step flow: pick clients & goals → capture content → generate summary → review & save.',
      'You can paste a transcript from Zoom, Otter.ai, Fireflies, or any tool that gives you text. You can also record live audio in the browser — it gets transcribed automatically.',
      'For group sessions, select multiple clients (or a group) on step 1 and the AI will tag action items by participant.',
      'On step 3, the AI generates a summary, key themes, breakthrough quotes, and suggested goals. You can edit any of it before saving.',
    ],
  },
  {
    id: 'sessions',
    icon: '📚',
    title: 'Sessions',
    summary: 'Browse and search every session across every client.',
    body: [
      'This is the master list of every session you\'ve recorded, sorted by date. Click a row to expand the full summary, transcript, and breakthrough quotes inline.',
      'Use the type filter at the top to show only 1:1 or only group sessions.',
      'You can also deep-link to a specific session — sessions opened from the Dashboard arrive here via a `?session=<id>` URL parameter.',
    ],
  },
  {
    id: 'schedule',
    icon: '🗓',
    title: 'Schedule',
    summary: 'Plan upcoming 1:1 and group sessions.',
    body: [
      'Add upcoming sessions with a date, time, duration, and which client (or group) you\'re seeing. You can also add private prep notes here.',
      'For any scheduled session, you can click "Generate prep brief" — the AI reads the last 3 sessions with that client (or group) and writes a pre-session brief: recent themes, open threads, suggested focus areas, and warm opening questions.',
    ],
  },
  {
    id: 'recordings',
    icon: '🎬',
    title: 'Pending Recordings',
    summary: 'Zoom cloud recordings show up here automatically when sessions end.',
    body: [
      'If you have Zoom Pro (or higher), your cloud recordings will automatically land here when a session ends. The system uses the host email on your Zoom account to match you — make sure your "notification email" in Settings matches that exact email.',
      'For each recording, click "Review & Import" → pick which clients were on the call → map speakers → create the session with an AI summary.',
      'Free Zoom plans don\'t support cloud recording. Use the manual New Session flow instead.',
    ],
  },
  {
    id: 'groups',
    icon: '👥',
    title: 'Groups',
    summary: 'Run group coaching containers with shared modalities and history.',
    body: [
      'A group is a named collection of clients you coach together. You can run group sessions across all members at once.',
      'Each group gets its own custom modality (built by the AI from the patterns it sees in your sessions with that group). This shapes how summaries are written for that group going forward.',
      'You can also connect a group Telegram chat to a group — messages flow bidirectionally and get included in session analysis.',
    ],
  },
  {
    id: 'marketing',
    icon: '📣',
    title: 'Marketing',
    summary: 'Turn breakthroughs into LinkedIn posts, IG captions, and bios.',
    body: [
      'Marketing has three tools: Wins (log client transformation wins, manually or auto-suggested from starred breakthrough quotes), Brag Reel (AI generates a LinkedIn post, Instagram caption, and testimonial blurb from your wins + quotes), and Bio (AI writes short and full coaching bios from your modalities and experience).',
      'All content is anonymized — no client names, no identifiable details — and written in a warm, authentic voice that avoids hype.',
    ],
  },
  {
    id: 'avatar',
    icon: '🧭',
    title: 'Client Avatars & Transformation Arcs',
    summary: 'AI-generated psychological profiles and longitudinal journey narratives.',
    body: [
      'After a few sessions with a client, you can click "Build avatar" on their profile. The AI reads all sessions and generates a profile covering communication style, motivations, resistance patterns, resonant language, and recommended coaching approach.',
      'The transformation arc is a narrative summary of how the client has changed over time — useful to share with the client at milestones or to refresh your memory before a session.',
      'Both are editable. Your judgment overrides the AI — the system stores whatever you save.',
    ],
  },
  {
    id: 'modalities',
    icon: '🌿',
    title: 'Modalities',
    summary: 'How your coaching approach shapes the AI.',
    body: [
      'During onboarding (and in Settings) you can multi-select the modalities you practice: Inner Child, Parts Work, Shadow Work, Transformational, NLP, Life Design, Somatic, and more.',
      'The AI combines the context instructions for every modality you select when generating session summaries. The more modalities you pick, the richer (but longer) the prompt.',
      'You can also add a free-text "custom approach" description — anything that\'s specific to how you work that the standard modality labels don\'t capture. This gets appended to the prompt verbatim.',
    ],
  },
  {
    id: 'portal',
    icon: '💞',
    title: 'Client Portal',
    summary: 'Your clients\' private view of their own journey.',
    body: [
      'Every client can have their own private portal — accessible on the web at a unique URL, or via the iOS client app (Android coming).',
      'In their portal, clients can: see their session history, view their goals and progress, exchange voice and text messages with you, and add their own goals.',
      'To set it up: open the client\'s profile → Overview tab → "Generate access code." Then share the access code and the portal URL with them. They\'ll set their own password on first login.',
    ],
  },
  {
    id: 'settings',
    icon: '⚙',
    title: 'Settings',
    summary: 'Your practice profile, integrations, and account controls.',
    body: [
      'The Practice tab is where you set your name, practice name, modalities, custom approach description, and notification email (used for Zoom auto-imports and client message alerts).',
      'The API Keys tab lets you save your own Anthropic API key — it\'s stored locally in your browser, not on our servers.',
      'The Integrations tab covers Zoom setup and other connections.',
      'The Data & Privacy tab lets you export your profile or delete your account (which permanently removes all your data — clients, sessions, messages, recordings, files).',
    ],
  },
];

const FEEDBACK_CATEGORIES = [
  { id: 'bug', label: '🐛 Bug report' },
  { id: 'feature', label: '✨ Feature request' },
  { id: 'enhancement', label: '⚡ Enhancement' },
  { id: 'general', label: '💬 General comment' },
  { id: 'would_pay', label: '💰 I would pay for...' },
];

export default function SupportWidget({ onReplayTour }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('help'); // 'help' | 'feedback'
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState('');
  const [feedback, setFeedback] = useState({ category: 'general', message: '', would_pay_for: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');

  const selected = ARTICLES.find(a => a.id === selectedId);

  const handleArticleClick = (article) => {
    if (article.isAction && article.id === 'tour') {
      setOpen(false);
      // defer so the panel close paints before Joyride mounts and looks for targets
      setTimeout(() => onReplayTour?.(), 100);
      return;
    }
    setSelectedId(article.id);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.message.trim()) return;
    setSubmitting(true);
    setFeedbackError('');
    try {
      await submitFeedback(feedback);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setOpen(false);
        setFeedback({ category: 'general', message: '', would_pay_for: '' });
      }, 2500);
    } catch (err) {
      setFeedbackError('Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setOpen(false);
    setSelectedId(null);
    setQuery('');
    setTab('help');
  };

  const filteredArticles = ARTICLES.filter(a => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q);
  });

  return (
    <>
      <button
        onClick={() => open ? closeAndReset() : setOpen(true)}
        style={styles.floatingBtn}
        title="Help & feedback"
      >
        {open ? '×' : '?'}
      </button>

      {open && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            {selected && (
              <button style={styles.backBtn} onClick={() => setSelectedId(null)} title="Back to list">←</button>
            )}
            <div style={styles.panelTitle}>
              {selected ? selected.title : (tab === 'help' ? 'Help & docs' : 'Send feedback')}
            </div>
            <div style={styles.panelBeta}>Beta</div>
          </div>

          {!selected && (
            <div style={styles.tabs}>
              <button
                style={{ ...styles.tab, ...(tab === 'help' ? styles.tabActive : {}) }}
                onClick={() => setTab('help')}
              >
                Help
              </button>
              <button
                style={{ ...styles.tab, ...(tab === 'feedback' ? styles.tabActive : {}) }}
                onClick={() => setTab('feedback')}
              >
                Feedback
              </button>
            </div>
          )}

          {/* HELP TAB — article list */}
          {!selected && tab === 'help' && (
            <div style={styles.listContent}>
              <input
                type="text"
                placeholder="Search articles…"
                style={styles.search}
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <div style={styles.list}>
                {filteredArticles.length === 0 && (
                  <div style={styles.emptyState}>No articles match "{query}"</div>
                )}
                {filteredArticles.map(article => (
                  <button
                    key={article.id}
                    style={styles.articleRow}
                    onClick={() => handleArticleClick(article)}
                  >
                    <span style={styles.articleIcon}>{article.icon}</span>
                    <span style={styles.articleText}>
                      <span style={styles.articleTitle}>{article.title}</span>
                      <span style={styles.articleSummary}>{article.summary}</span>
                    </span>
                    <span style={styles.articleArrow}>›</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* HELP TAB — article detail */}
          {selected && selected.body && (
            <div style={styles.articleContent}>
              {selected.body.map((para, i) => (
                <p key={i} style={styles.para}>{para}</p>
              ))}
            </div>
          )}

          {/* FEEDBACK TAB */}
          {!selected && tab === 'feedback' && (
            submitted ? (
              <div style={styles.successState}>
                <div style={styles.successIcon}>✓</div>
                <div style={styles.successText}>Thank you! Your feedback helps shape the product.</div>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} style={styles.feedbackForm}>
                {feedbackError && <div style={styles.errorAlert}>{feedbackError}</div>}
                <label style={styles.label}>Category</label>
                <div style={styles.categoryBtns}>
                  {FEEDBACK_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      style={{ ...styles.categoryBtn, ...(feedback.category === cat.id ? styles.categoryBtnActive : {}) }}
                      onClick={() => setFeedback({ ...feedback, category: cat.id })}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                <label style={styles.label}>
                  {feedback.category === 'bug' && 'Describe the bug'}
                  {feedback.category === 'feature' && 'Describe the feature'}
                  {feedback.category === 'enhancement' && 'What would you improve?'}
                  {feedback.category === 'general' && 'Your thoughts'}
                  {feedback.category === 'would_pay' && 'What would you pay for?'}
                </label>
                <textarea
                  style={styles.textarea}
                  value={feedback.message}
                  onChange={e => setFeedback({ ...feedback, message: e.target.value })}
                  placeholder={
                    feedback.category === 'bug' ? 'What happened? What did you expect to happen?' :
                    feedback.category === 'feature' ? 'Describe the feature and why it would help...' :
                    feedback.category === 'enhancement' ? 'What would make this better?' :
                    feedback.category === 'would_pay' ? 'Describe what you\'d pay for and roughly how much...' :
                    'Share anything on your mind...'
                  }
                  required
                />

                {feedback.category === 'would_pay' && (
                  <>
                    <label style={styles.label}>Rough price point (optional)</label>
                    <input
                      style={styles.input}
                      placeholder="e.g. $29/month, $10/session..."
                      value={feedback.would_pay_for}
                      onChange={e => setFeedback({ ...feedback, would_pay_for: e.target.value })}
                    />
                  </>
                )}

                <button
                  type="submit"
                  style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send feedback'}
                </button>
              </form>
            )
          )}

          <div style={styles.panelFooter}>
            {tab === 'help' && !selected ? 'Can\'t find what you need? Switch to Feedback to ask.' : 'Beta · Your feedback shapes the product'}
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  floatingBtn: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#C4956A',
    border: 'none',
    color: 'white',
    fontSize: '22px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Lora', serif",
  },
  panel: {
    position: 'fixed',
    bottom: '84px',
    right: '24px',
    width: '380px',
    maxHeight: '72vh',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    border: '1px solid #D8E0DC',
    zIndex: 1000,
    overflow: 'hidden',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    padding: '14px 18px',
    borderBottom: '1px solid #D8E0DC',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#F8F5F0',
    flexShrink: 0,
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2D3A35',
    flex: 1,
    fontFamily: "'Lora', serif",
  },
  panelBeta: {
    background: '#EBF0ED',
    color: '#5C7A6B',
    padding: '2px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#5C7A6B',
    fontSize: '18px',
    padding: '0 4px',
    lineHeight: 1,
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #D8E0DC',
    background: '#fff',
    flexShrink: 0,
  },
  tab: {
    flex: 1,
    background: 'none',
    border: 'none',
    padding: '12px 0',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: "'DM Sans', sans-serif",
    color: '#7A8C86',
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    color: '#5C7A6B',
    borderBottom: '2px solid #5C7A6B',
    fontWeight: '600',
  },
  listContent: {
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  search: {
    margin: '14px 18px 6px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #D8E0DC',
    background: '#F8F5F0',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '13px',
    color: '#2D3A35',
    outline: 'none',
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '6px 0 10px',
  },
  emptyState: {
    padding: '24px 20px',
    textAlign: 'center',
    color: '#7A8C86',
    fontSize: '13px',
  },
  articleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 18px',
    background: 'none',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'DM Sans', sans-serif",
    borderBottom: '1px solid #F0EBE4',
  },
  articleIcon: {
    fontSize: '18px',
    flexShrink: 0,
    marginTop: '2px',
  },
  articleText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    flex: 1,
    minWidth: 0,
  },
  articleTitle: {
    fontSize: '13.5px',
    fontWeight: '600',
    color: '#1C2B26',
  },
  articleSummary: {
    fontSize: '12px',
    color: '#7A8C86',
    lineHeight: 1.4,
  },
  articleArrow: {
    color: '#C4956A',
    fontSize: '18px',
    flexShrink: 0,
    alignSelf: 'center',
  },
  articleContent: {
    padding: '18px 20px',
    overflowY: 'auto',
    flex: 1,
  },
  para: {
    fontSize: '13.5px',
    color: '#4A5A54',
    lineHeight: 1.65,
    marginBottom: '12px',
  },
  feedbackForm: {
    padding: '14px 18px 0',
    overflowY: 'auto',
    flex: 1,
  },
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: '#7A8C86',
    marginBottom: '8px',
    marginTop: '14px',
  },
  categoryBtns: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  categoryBtn: {
    padding: '5px 10px',
    borderRadius: '20px',
    border: '1px solid #D8E0DC',
    background: 'none',
    color: '#4A5A54',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  categoryBtnActive: {
    background: '#5C7A6B',
    borderColor: '#5C7A6B',
    color: 'white',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #D8E0DC',
    background: '#F8F5F0',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '13.5px',
    color: '#2D3A35',
    outline: 'none',
    resize: 'vertical',
    minHeight: '100px',
    boxSizing: 'border-box',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #D8E0DC',
    background: '#F8F5F0',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '13.5px',
    color: '#2D3A35',
    outline: 'none',
    boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%',
    margin: '16px 0',
    padding: '11px',
    borderRadius: '8px',
    background: '#5C7A6B',
    border: 'none',
    color: 'white',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '13.5px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  successState: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  successIcon: {
    fontSize: '32px',
    color: '#5C7A6B',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '14px',
    color: '#4A5A54',
    lineHeight: 1.6,
  },
  errorAlert: {
    background: '#F5EAEA',
    color: '#B85C5C',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '8px',
  },
  panelFooter: {
    padding: '10px 18px',
    borderTop: '1px solid #D8E0DC',
    fontSize: '11px',
    color: '#7A8C86',
    textAlign: 'center',
    background: '#F8F5F0',
    flexShrink: 0,
  },
};
